import React, { useState, useEffect, lazy, Suspense, Profiler, ProfilerOnRenderCallback } from 'react';
import { LanguageProvider } from './context/LanguageContext';
import { SEO } from './components/SEO';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { TrustedBySlider } from './components/TrustedBySlider';
import { AboutSection } from './components/AboutSection';
import { ServicesSection } from './components/ServicesSection';
import { WorkflowSection } from './components/WorkflowSection';
import { WhyChooseUs } from './components/WhyChooseUs';
import { FaqSection } from './components/FaqSection';
import { TechnologiesSection } from './components/TechnologiesSection';
import { BlogSection } from './components/BlogSection';
import { ProcessTimeline } from './components/ProcessTimeline';
import { TestimonialsSection } from './components/TestimonialsSection';
import { ContactSection } from './components/ContactSection';
import { CareersPage } from './components/CareersPage';
import { SelfPublishingPage } from './components/SelfPublishingPage';
import { Footer } from './components/Footer';
import { useWebVitals } from './hooks/useWebVitals';

// React Profiler callback for monitoring render bottlenecks
const onRenderReportBottlenecks: ProfilerOnRenderCallback = (
  id,
  phase,
  actualDuration,
  baseDuration
) => {
  if (actualDuration > 10) {
    console.warn(
      `[React Profiler Bottleneck] Component '${id}' (${phase}) took ${actualDuration.toFixed(2)}ms to render (base: ${baseDuration.toFixed(2)}ms).`
    );
  } else {
    console.debug(
      `[React Profiler] '${id}' (${phase}) rendered in ${actualDuration.toFixed(2)}ms.`
    );
  }
};

// Performance Optimization: Lazy Load Modals and Floating Chat Widget
const ProjectEstimatorModal = lazy(() =>
  import('./components/ProjectEstimatorModal').then((m) => ({ default: m.ProjectEstimatorModal }))
);
const LegalModal = lazy(() =>
  import('./components/LegalModal').then((m) => ({ default: m.LegalModal }))
);
const SecurityPerformanceModal = lazy(() =>
  import('./components/SecurityPerformanceModal').then((m) => ({ default: m.SecurityPerformanceModal }))
);
const AIChatBot = lazy(() =>
  import('./components/AIChatBot').then((m) => ({ default: m.AIChatBot }))
);
const EmailPreviewAdmin = lazy(() =>
  import('./components/EmailPreviewAdmin').then((m) => ({ default: m.EmailPreviewAdmin }))
);

export default function App() {
  const [currentView, setCurrentView] = useState<'home' | 'careers' | 'self-publishing'>('home');
  const [isEstimatorOpen, setIsEstimatorOpen] = useState<boolean>(false);
  const [isSecurityAuditOpen, setIsSecurityAuditOpen] = useState<boolean>(false);
  const [isLegalOpen, setIsLegalOpen] = useState<boolean>(false);

  // Monitor Core Web Vitals (LCP, CLS, FCP, TTFB)
  useWebVitals();
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState<boolean>(false);
  const [legalDocType, setLegalDocType] = useState<string>('privacy-policy');
  const [contactInitialService, setContactInitialService] = useState<string>('');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentView]);

  useEffect(() => {
    // 1. Disable browser automatic scroll restoration on refresh/reload
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // 2. Always set current view to 'home' and scroll to absolute top on load
    setCurrentView('home');
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // 3. Clear initial hash on website load to guarantee landing on Home top
    if (window.location.hash) {
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (e) {
        window.location.hash = '';
      }
    }

    // 4. Handle subsequent URL hash / popstate routing during active user browsing
    const handleUrlRouting = () => {
      const hash = window.location.hash.toLowerCase();
      const pathname = window.location.pathname.toLowerCase();
      const searchParams = new URLSearchParams(window.location.search);
      const pageParam = searchParams.get('page')?.toLowerCase() || searchParams.get('view')?.toLowerCase();

      if (
        hash === '#self-publishing' ||
        hash === '#authors' ||
        hash === '#publish' ||
        hash === '#self-help' ||
        pageParam === 'self-publishing' ||
        pageParam === 'selfpublishing' ||
        pageParam === 'publish'
      ) {
        setCurrentView('self-publishing');
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } else if (
        hash === '#careers' ||
        pageParam === 'careers'
      ) {
        setCurrentView('careers');
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } else if (
        hash === '#email-preview' ||
        hash === '#admin/email-preview' ||
        hash === '#admin' ||
        hash === '#admin-dashboard' ||
        pathname.includes('email-preview')
      ) {
        setIsEmailPreviewOpen(true);
      }
    };

    window.addEventListener('hashchange', handleUrlRouting);
    window.addEventListener('popstate', handleUrlRouting);
    return () => {
      window.removeEventListener('hashchange', handleUrlRouting);
      window.removeEventListener('popstate', handleUrlRouting);
    };
  }, []);

  const navigateToHome = () => {
    setCurrentView('home');
    if (window.location.hash === '#self-publishing' || window.location.hash === '#careers') {
      try {
        history.pushState('', document.title, window.location.pathname + window.location.search);
      } catch (e) {
        window.location.hash = '';
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const navigateToCareers = () => {
    setCurrentView('careers');
    window.location.hash = 'careers';
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const navigateToSelfPublishing = () => {
    setCurrentView('self-publishing');
    window.location.hash = 'self-publishing';
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleOpenContact = (serviceTitle?: string) => {
    const scrollToContactWithOffset = () => {
      if (serviceTitle) {
        setContactInitialService(serviceTitle);
      }
      const contactElem = document.getElementById('contact');
      if (contactElem) {
        const headerOffset = 80; // offset so header doesn't hide the section title
        const elementPosition = contactElem.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    if (currentView !== 'home') {
      setCurrentView('home');
      setTimeout(() => {
        scrollToContactWithOffset();
      }, 120);
    } else {
      scrollToContactWithOffset();
    }
  };

  const handleOpenEstimator = () => {
    setIsEstimatorOpen(true);
  };

  const handleOpenContactWithEstimate = (estimateSummary: string) => {
    setContactInitialService(`Estimate Request: ${estimateSummary}`);
    handleOpenContact();
  };

  // Structured Data JSON-LD Schema for Organization and ProfessionalService
  const structuredDataJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "EducationalOrganization", "ProfessionalService"],
        "@id": "https://www.asaplearning.in/#organization",
        "name": "ASAP LEARNING",
        "legalName": "ASAP LEARNING Pvt. Ltd.",
        "url": "https://www.asaplearning.in/",
        "logo": "https://www.asaplearning.in/logo.png",
        "email": "contact@asaplearning.in",
        "telephone": "+91-7817899559",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Dehradun Production Center, Uttam Nagar",
          "addressLocality": "Dehradun",
          "addressRegion": "Uttarakhand",
          "postalCode": "248001",
          "addressCountry": "IN"
        }
      },
      {
        "@type": "ProfessionalService",
        "@id": "https://www.asaplearning.in/#professionalservice",
        "name": "ASAP LEARNING Prepress & Web Engineering Services",
        "url": "https://www.asaplearning.in/",
        "telephone": "+91-7817899559",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Dehradun Production Center, Uttam Nagar",
          "addressLocality": "Dehradun",
          "addressRegion": "Uttarakhand",
          "postalCode": "248001",
          "addressCountry": "IN"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://www.asaplearning.in/#breadcrumb",
        "itemListElement": currentView === 'self-publishing' ? [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.asaplearning.in/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Self-Publishing & Author Studio",
            "item": "https://www.asaplearning.in/#self-publishing"
          }
        ] : currentView === 'careers' ? [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.asaplearning.in/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Careers & Opportunities",
            "item": "https://www.asaplearning.in/#careers"
          }
        ] : [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.asaplearning.in/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Publishing & Web Services",
            "item": "https://www.asaplearning.in/#services"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "About ASAP LEARNING",
            "item": "https://www.asaplearning.in/#about"
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "Contact HQ",
            "item": "https://www.asaplearning.in/#contact"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://www.asaplearning.in/#faqpage",
        "url": "https://www.asaplearning.in/#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How does ASAP LEARNING combine AI with traditional typesetting precision?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ASAP LEARNING leverages AI-assisted automated workflow engines to ingest raw manuscripts, parse structural styles, and auto-populate templates in InDesign and LaTeX. Experienced human typographers then review typography, pagination, baseline grids, and line-breaks to guarantee 100% publisher-grade accuracy."
            }
          },
          {
            "@type": "Question",
            "name": "What quality control measures ensure error-free XML and PDF deliverables?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our multi-tier QA protocol combines automated schema validators (BITS, JATS, DocBook, EPUB3) with rigorous manual proofing. Every equation, reference citation, and table is cross-checked against strict STM and ISO guidelines before final delivery."
            }
          },
          {
            "@type": "Question",
            "name": "What is the typical turnaround time for complex academic or technical books?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Thanks to our automated prepress pipelines, standard turnaround times are reduced by up to 60%. A typical 300-page academic book or journal volume is completed within 3 to 7 business days, with express 24-48 hour turnaround available for urgent releases."
            }
          },
          {
            "@type": "Question",
            "name": "Which input and output formats do you support for digital and print publishing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We accept MS Word (.docx), TeX/LaTeX, PDF, and raw text. We deliver print-ready PDFs with high-res vector graphics, reflowable EPUB3, accessible PDF/UA (ISO 14289-1), JATS 1.3 / BITS 2.1 XML, HTML5, and custom structured XML schema according to your specifications."
            }
          }
        ]
      }
    ]
  };

  return (
    <LanguageProvider>
      <div className="min-h-screen bg-white text-slate-900 font-sans antialiased selection:bg-blue-600 selection:text-white">
      {/* JSON-LD Script for Organization & ProfessionalService */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredDataJsonLd) }}
      />

      {/* Dynamic SEO Meta Tags & JSON-LD Structured Data */}
      {currentView === 'self-publishing' ? (
        <SEO
          title="Self-Publishing Studio for Authors & Creators | ASAP LEARNING"
          description="Transform raw manuscripts into print-ready PDF, Kindle EPUB3, custom cover artwork, and ISBN-ready files. Complete KDP, IngramSpark & Barnes & Noble self-publishing solutions."
          keywords="Self-Publishing Services, Author Book Design, KDP Formatting, IngramSpark Print Ready, Kindle EPUB Conversion, Manuscript Typesetting, Book Cover Design, Indexing Services, ISBN Registration, Independent Author Publishing, ASAP LEARNING Authors"
          canonicalUrl="https://www.asaplearning.in/#self-publishing"
          ogImage="https://www.asaplearning.in/og-self-publishing.jpg"
        />
      ) : currentView === 'careers' ? (
        <SEO
          title="Careers & Freelance Publishing Roles | ASAP LEARNING"
          description="Join ASAP LEARNING as a remote Desktop Publisher, Typesetting Specialist, JATS XML Conversion Editor, or PDF Accessibility Analyst. Apply for full-time & freelance opportunities."
          keywords="ASAP LEARNING Careers, Publishing Jobs, InDesign Freelancer, Typesetter Jobs, JATS XML Specialist, PDF Accessibility Remediation Jobs, Remote Prepress Jobs, India Publishing Jobs"
          canonicalUrl="https://www.asaplearning.in/#careers"
          ogImage="https://www.asaplearning.in/og-careers.jpg"
        />
      ) : (
        <SEO
          title="ASAP LEARNING | Prepress, Typesetting, JATS XML & PDF/UA Accessibility"
          description="ASAP LEARNING provides automated publishing solutions: Adobe InDesign typesetting, JATS 1.3 / BITS 2.1 XML conversion, EPUB3 creation, and 100% compliant PDF/UA accessibility."
          keywords="ASAP LEARNING, Prepress Automation, Typesetting Services, JATS XML, BITS XML, PDF UA Accessibility, InDesign Automation, STM Journal Publishing, EPUB3 Conversion, Publishing Solutions India"
          canonicalUrl="https://www.asaplearning.in/"
          ogImage="https://www.asaplearning.in/og-image.jpg"
        />
      )}

      {/* Navigation Header */}
      <Header
        currentView={currentView}
        onNavigateHome={navigateToHome}
        onNavigateCareers={navigateToCareers}
        onNavigateSelfPublishing={navigateToSelfPublishing}
        onOpenEstimator={handleOpenEstimator}
        onOpenSecurityAudit={() => setIsSecurityAuditOpen(true)}
        onOpenContact={() => handleOpenContact()}
      />

      {/* Main Content View Switcher wrapped with React Profiler */}
      <Profiler id="MainPageLayout" onRender={onRenderReportBottlenecks}>
        {currentView === 'self-publishing' ? (
          <SelfPublishingPage
            onBackToHome={navigateToHome}
            onOpenContact={(serviceTitle) => handleOpenContact(serviceTitle)}
          />
        ) : currentView === 'careers' ? (
          <CareersPage
            onBackToHome={navigateToHome}
            onOpenContact={() => handleOpenContact()}
          />
        ) : (
          <main>
            {/* Hero Section */}
            <Hero
              onGetStarted={() => handleOpenContact()}
              onContactUs={() => handleOpenContact()}
              onOpenEstimator={handleOpenEstimator}
              onOpenSecurityAudit={() => setIsSecurityAuditOpen(true)}
            />

            {/* Trusted By Social Proof Logo Slider */}
            <TrustedBySlider />

            {/* About Section */}
            <AboutSection />

            {/* Our Services Section */}
            <ServicesSection
              onSelectServiceForQuote={(serviceTitle) => handleOpenContact(serviceTitle)}
            />

            {/* Automation Workflow Section */}
            <WorkflowSection onOpenContact={() => handleOpenContact()} />

            {/* Technologies We Use Section */}
            <TechnologiesSection />

            {/* Process Roadmap */}
            <ProcessTimeline />

            {/* Client Testimonials Section */}
            <TestimonialsSection />

            {/* Why Choose Us Section */}
            <WhyChooseUs
              onOpenEstimator={handleOpenEstimator}
            />

            {/* Contact & Lead Capture Section */}
            <ContactSection
              initialServiceRequirement={contactInitialService}
            />

            {/* 100% Automated Weekly Blog Section */}
            <BlogSection
              onOpenContact={() => handleOpenContact()}
            />

            {/* Frequently Asked Questions (FAQ) Section */}
            <FaqSection
              onOpenContact={() => handleOpenContact()}
            />
          </main>
        )}
      </Profiler>

      {/* Footer */}
      <Footer
        onNavigateHome={navigateToHome}
        onNavigateCareers={navigateToCareers}
        onNavigateSelfPublishing={navigateToSelfPublishing}
        onOpenEstimator={handleOpenEstimator}
        onOpenContact={() => handleOpenContact()}
        onOpenEmailPreview={() => setIsEmailPreviewOpen(true)}
        onOpenLegal={(docType) => {
          if (docType) setLegalDocType(docType);
          setIsLegalOpen(true);
        }}
      />

      {/* Deferred Load Widgets & Modals for Maximum Performance */}
      <Suspense fallback={null}>
        {/* Email Acknowledgment Template Previewer (Hidden Admin Suite) */}
        {isEmailPreviewOpen && (
          <EmailPreviewAdmin
            isOpen={isEmailPreviewOpen}
            onClose={() => setIsEmailPreviewOpen(false)}
          />
        )}

        {/* Interactive Turnaround Estimator Modal */}
        {isEstimatorOpen && (
          <ProjectEstimatorModal
            isOpen={isEstimatorOpen}
            onClose={() => setIsEstimatorOpen(false)}
            onOpenContactWithEstimate={handleOpenContactWithEstimate}
          />
        )}

        {/* Legal & Compliance Governance Modal */}
        {isLegalOpen && (
          <LegalModal
            isOpen={isLegalOpen}
            initialDocType={legalDocType}
            onClose={() => setIsLegalOpen(false)}
          />
        )}

        {/* Security Badges & Performance Audit Modal */}
        {isSecurityAuditOpen && (
          <SecurityPerformanceModal
            isOpen={isSecurityAuditOpen}
            onClose={() => setIsSecurityAuditOpen(false)}
            onOpenContactWithAudit={(auditSummary) => {
              setContactInitialService(`Audit Inquiry: ${auditSummary}`);
              handleOpenContact();
            }}
          />
        )}

        {/* Floating AI Publishing Assistant Widget */}
        <AIChatBot
          onOpenEstimator={handleOpenEstimator}
          onOpenContact={() => handleOpenContact()}
        />
      </Suspense>
    </div>
    </LanguageProvider>
  );
}

